import { useMemo } from 'react';

import { transformReviewBillFromServer } from '../../common/utils/billUtils';
import { requestBuilder } from '../../http/RequestBuilder';
import type { IBill } from '../types/Bill';
import type { BillDiscoveryReviewAccount } from '../types/BillDiscoveryAccount';

interface BillDiscoveryReviewResponse {
    bills: IBill[];
    billAccounts: BillDiscoveryReviewAccount[];
}

const getBillDiscoveryReview = requestBuilder.query<
    BillDiscoveryReviewResponse,
    { membershipId: string }
>({
    endpoint: ({ membershipId }) =>
        `/api/holistic-bills/${membershipId}/discovery-review`,
    queryKey: ({ membershipId }) => `useGetBillDiscoveryReview/${membershipId}`,
});

const useGetBillDiscoveryReview = (
    membershipId: string,
    isLoadingHolisticSummary?: boolean,
) => {
    const queryInfo = getBillDiscoveryReview(
        { membershipId },
        {
            enabled: membershipId !== '' && !isLoadingHolisticSummary,
            gcTime: 0,
        },
    );

    // Transform the data from the server
    const bills = queryInfo.data
        ? queryInfo.data.bills.map((bill) =>
              transformReviewBillFromServer(bill),
          )
        : [];
    const billAccounts = queryInfo.data ? queryInfo.data.billAccounts : [];

    const memoed = useMemo(() => {
        return queryInfo.data
            ? {
                  ...queryInfo.data,
                  bills,
                  billAccounts,
              }
            : {
                  bills: [],
                  billAccounts: [],
              };
    }, [queryInfo.data?.billAccounts.length, queryInfo.data?.bills.length]);

    return {
        ...queryInfo,
        data: memoed,
    };
};

export default useGetBillDiscoveryReview;
